import type React from "react"
import { useState, useEffect } from "react"
import ComponentCard from "../../components/common/ComponentCard"
import Button from "../../components/ui/button/Button"
import Badge from "../../components/ui/badge/Badge"
import LineChartOne from "../../components/charts/line/LineChartOne"
import useApi from "../../hooks/useApi"
import { toast } from "react-hot-toast"

interface AnalisisClientesProps {
  mostPaid: string
  leastPaid: string
  topAgencyMetric: string
}

const AnalisisClientes: React.FC<AnalisisClientesProps> = ({ 
  mostPaid, 
  leastPaid, 
  topAgencyMetric 
}) => {
  const [chartData, setChartData] = useState<{ name: string; data: number[]; }[]>([]);
  const { fetchRecentBookings, processBookingTrends } = useApi();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const bookingsData = await fetchRecentBookings();
        setChartData(processBookingTrends(bookingsData));
      } catch (error) {
        toast.error("Error fetching data")
      }
    };
    fetchData();
  }, [fetchRecentBookings, processBookingTrends]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
        Análisis y reportes
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ComponentCard title="Tendencia de reservas" className="col-span-1 lg:col-span-2">
          <div className="w-full h-80">
            {chartData && <LineChartOne series={chartData} />}
          </div>
        </ComponentCard>

        <ComponentCard title="Clientes por monto pagado" className="col-span-1">
          <div className="space-y-6 p-4">
            <div>
              <div className="mb-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                Mayor facturación
              </div>
              <div className="flex items-center justify-between">
                <div className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                  {mostPaid.split('(')[0]}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {mostPaid.includes('(') ? `(${mostPaid.split('(')[1]}` : ''}
                  </span>
                  <Badge variant="light" color="success" size="sm">
                    Alto
                  </Badge>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                Menor facturación
              </div>
              <div className="flex items-center justify-between">
                <div className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                  {leastPaid.split('(')[0]}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {leastPaid.includes('(') ? `(${leastPaid.split('(')[1]}` : ''}
                  </span>
                  <Badge variant="light" color="error" size="sm">
                    Bajo
                  </Badge>
                </div>
              </div>
            </div>

            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Clientes con mayor y menor monto pagado en los últimos 30 días.
            </p>
          </div>
        </ComponentCard>

        <ComponentCard title="Agencias más rentables" className="col-span-1">
          <div className="p-4">
            <div className="mb-2 text-sm font-medium text-gray-500 dark:text-gray-400">
              Mejor desempeño
            </div>
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                {topAgencyMetric.split('(')[0]}
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge variant="light" color="success" size="sm">
                  Destacado
                </Badge>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  {topAgencyMetric.includes('(') ? `(${topAgencyMetric.split('(')[1]}` : ''}
                </span>
              </div>
            </div>

            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Ranking de agencias con mayor volumen de reservas y rentabilidad.
            </p>
          </div>
        </ComponentCard>
      </div>
    </div>
  )
}

export default AnalisisClientes